/** "Run now", with the sites it covers and the operator's note for the run.
 *
 * Shared by the agent's own Scheduled tab (the whole portfolio) and a project's
 * Scheduled tab (that site alone), so the two cannot drift on what the note does
 * or on how a refusal reads. See
 * docs/Y2026-Q3/design-operator-scoped-agent-runs.md.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

import { useRunSweepNow } from "../api/hooks/useAgentPortfolio";
import { sendOnCmdEnter } from "../utils/sendShortcut";
import { BlockingRuns } from "./BlockingRuns";
import { ProjectPicker } from "./ProjectPicker";
import controls from "./FormControls.module.css";
import styles from "./RunNowPanel.module.css";

/** What an untouched picker is actually going to run, said in the control.
 *
 * Kept short on purpose: ProjectPicker's toggle caps at 320px and ellipses what
 * does not fit, so the COUNT goes here and the criteria that produced it go in
 * the note beside it, which wraps freely. */
const autoPickedLabel = (count: number | undefined): string => {
  if (count === undefined) return "whatever the plan finds";
  if (count === 0) return "none auto-picked";
  return `${count} site${count === 1 ? "" : "s"} auto-picked`;
};

export const RunNowPanel = ({
  agent,
  pageLabel,
  projectIds,
  note,
  placeholder,
  disabled = false,
  buttonLabel = "Run now",
  choosableSites = false,
  chooseNote,
  autoPickedCount,
  runHref,
}: {
  /** The agent whose page this is, or null on a project page - the team's run is
   * the Manager's whoever asks, so the server fills it in. */
  agent: string | null;
  /** The page the panel is on, shown on the run it starts ("Scheduled"). */
  pageLabel: string;
  /** Fixed sites this panel always runs, used by a project's own page. Mutually
   * exclusive with `choosableSites`, which lets the operator pick instead. */
  projectIds?: string[];
  /** What the panel says above the box. */
  note: string;
  placeholder: string;
  /** Normally "the plan found nothing to do". Overridden by a site choice: that
   * is the whole point of offering one. */
  disabled?: boolean;
  buttonLabel?: string;
  /** Offer a site picker, so the operator can run projects the plan skipped. */
  choosableSites?: boolean;
  chooseNote?: string;
  /** How many sites the plan picked itself, so an untouched picker can say what
   * leaving it alone will do. */
  autoPickedCount?: number;
  /** Where a started run lives. Given, the page opens it as soon as it starts. */
  runHref?: (runId: string) => string;
}) => {
  const runNow = useRunSweepNow(agent);
  const navigate = useNavigate();
  const { user } = useKindeAuth();
  const [prompt, setPrompt] = useState("");
  const [chosen, setChosen] = useState<string[]>([]);
  // Off unless asked: the schedule's own pass always reviews, a hand-started one costs
  // up to $8 more a site only when the operator wants it.
  const [review, setReview] = useState(false);
  const sites = choosableSites ? chosen : (projectIds ?? []);
  // A site the operator named is worked whether or not anything arrived on it,
  // so naming one is exactly the case where "nothing to do" must stop blocking.
  const blocked = disabled && sites.length === 0;
  // One press per page. A run that started is in flight for tens of minutes and
  // the second press is refused ("already in flight"), which reads as a fault
  // rather than as "you already did this" - so the button says so itself, until
  // the page is next loaded. A REFUSED start leaves it pressable, because the
  // thing to do then is fix what it said and try again.
  const started = runNow.data?.status === "started";

  const start = () => {
    if (runNow.isLoading || blocked || started) return;
    runNow.mutate(
      {
        prompt: prompt.trim() || undefined,
        project_ids: sites.length > 0 ? sites : undefined,
        // Shown as who started the run; without it the run reads "Automated".
        actor_email: user?.email ?? "",
        started_from: pageLabel,
        automated_review: review,
      },
      {
        onSuccess: (result) => {
          if (runHref && result.status === "started" && result.run_id) {
            navigate(runHref(result.run_id));
          }
        },
      },
    );
  };

  return (
    <div className={styles.panel}>
      <p className={styles.muted}>{note}</p>
      {choosableSites && (
        <div className={`${styles.row} ${styles.sitesRow}`}>
          <span className={controls.control}>
            Run over sites:
            <ProjectPicker
              projectIds={chosen}
              // The server refuses a site nobody is enrolled on, so offering one
              // could only produce a refusal.
              scheduledOnly
              // Empty is not "none" - it is the plan's own selection, and the
              // operator needs to know what that came to and why before
              // deciding whether to override it.
              placeholder={autoPickedLabel(autoPickedCount)}
              onChange={setChosen}
            />
          </span>
          {chooseNote && <span className={styles.muted}>{chooseNote}</span>}
        </div>
      )}
      <textarea
        className={styles.composer}
        rows={2}
        value={prompt}
        placeholder={placeholder}
        onChange={(e) => setPrompt(e.target.value)}
        onKeyDown={sendOnCmdEnter(start)}
      />
      <label className={styles.reviewToggle}>
        <input
          type="checkbox"
          checked={review}
          disabled={runNow.isLoading || started}
          onChange={(e) => setReview(e.target.checked)}
        />
        <span>
          Include the automated reviewer
          <span className={styles.muted}>
            {" "}
            - on sites that have it switched on, up to $8 a site. The nightly run
            always includes it.
          </span>
        </span>
      </label>
      <div className={styles.row}>
        <button
          type="button"
          className={styles.primary}
          disabled={runNow.isLoading || blocked || started}
          onClick={start}
        >
          {runNow.isLoading
            ? "Starting…"
            : started
              ? "Started"
              : sites.length > 0 && choosableSites
                ? `Run now on ${sites.length} site${sites.length === 1 ? "" : "s"}`
                : buttonLabel}
        </button>
        {/* The server distinguishes started / already running / nothing enrolled,
            and each needs saying - a silent button is indistinguishable from a
            refused one. */}
        {runNow.data && (
          <span className={styles.muted}>
            {runNow.data.detail}
            {(runNow.data.waiting_on?.length ?? 0) > 0 && (
              <>
                {" "}
                Waiting on <BlockingRuns runs={runNow.data.waiting_on ?? []} />.
              </>
            )}
          </span>
        )}
        {runNow.error && <span className={styles.error}>{runNow.error.message}</span>}
      </div>
    </div>
  );
};
